import React from "react";
import { motion } from "framer-motion";
import { Droplets, Sparkles, Sun, Car, Check } from "lucide-react";
import { SITE } from "@/data/site";
import { useProgram } from "@/lib/programs";
import { czk } from "@/lib/price";

const iconMap = { Droplets, Sparkles, Sun, Car };

export default function Services() {
  const { program } = useProgram();
  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative bg-brand-bg py-24 lg:py-32 border-t border-brand-border"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        {/* Header */}
        <div className="grid lg:grid-cols-12 gap-10 items-end mb-16">
          <div className="lg:col-span-7">
            <div className="text-xs uppercase tracking-[0.3em] text-brand-accent">
              Program {czk(program.price)} · co je v ceně
            </div>
            <h2 className="mt-4 font-display font-black text-4xl lg:text-6xl leading-[1] tracking-tight">
              Co{" "}
              <span className="italic font-light">přesně</span> se s vaším autem stane.
            </h2>
          </div>
          <div className="lg:col-span-5 lg:pl-10">
            <p className="text-brand-muted leading-relaxed">
              Žádné překvapení, žádný drobný tisk. Kompletní program zahrnuje čtyři
              precizně provedené kroky — od povrchního mytí až po hloubkové ošetření laku
              a interiéru.
            </p>
          </div>
        </div>

        {/* Service cards */}
        <div className="grid md:grid-cols-2 gap-px bg-brand-border">
          {SITE.services.map((svc, idx) => {
            const Icon = iconMap[svc.icon] || Droplets;
            const img = [
              SITE.images.exterior,
              SITE.images.decontamination,
              SITE.images.polishing,
              SITE.images.interior,
            ][idx];
            return (
              <motion.article
                key={svc.n}
                data-testid={`service-card-${svc.n}`}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.6, delay: idx * 0.08 }}
                className="relative group bg-brand-bg p-8 lg:p-10 overflow-hidden hover:bg-brand-surface transition-colors"
              >
                {/* Background image fade */}
                <div
                  className="absolute inset-0 opacity-[0.08] group-hover:opacity-[0.15] transition-opacity bg-cover bg-center"
                  style={{ backgroundImage: `url(${img})` }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-brand-bg via-brand-bg/80 to-transparent" />

                <div className="relative z-10">
                  <div className="flex items-start justify-between">
                    <span className="font-display text-6xl lg:text-7xl font-black text-brand-border group-hover:text-brand-accent transition-colors">
                      {svc.n}
                    </span>
                    <div className="w-12 h-12 border border-brand-border flex items-center justify-center group-hover:border-brand-accent group-hover:bg-brand-accent/10 transition-all">
                      <Icon size={20} className="text-brand-accent" />
                    </div>
                  </div>

                  <h3 className="mt-6 font-display font-bold text-2xl lg:text-3xl">
                    {svc.title}
                  </h3>
                  <p className="mt-3 text-brand-muted text-base leading-relaxed">{svc.summary}</p>

                  <ul className="mt-6 space-y-2.5 text-sm">
                    {svc.bullets.map((b, i) => (
                      <li key={i} className="flex gap-3 text-white/80">
                        <Check size={16} className="mt-0.5 shrink-0 text-brand-accent" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
